<script lang="ts" setup>
  import { useQuery, useQueryCache } from '@pinia/colada';
  import { computed, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { api, ApiError } from '../../../../shared/api/client.ts';
  import type { GameMode } from '../../../../shared/api/game.ts';
  import { playerSessionQuery } from '../../../../shared/api/player.ts';
  import { hostPlaylistsQuery } from '../../../../shared/api/playlists.ts';
  import { messageOf } from '../../../../shared/api/serverFailure.ts';
  import { useFlag } from '../../../../shared/config/flags.ts';
  import { route } from '../../../../shared/config/routes.ts';
  import { startUserAction, userAction } from '../../../../shared/observability/userAction.ts';
  import { usePlayerSession } from '../../model/playerSession.ts';
  import RegistrationView from '../RegistrationView.vue';
  import HostGameModeChoice from './HostGameModeChoice.vue';
  import HostRejoinAction from './HostRejoinAction.vue';

  const router = useRouter();
  const nickname = ref<string>('');
  const err = ref<string>('');
  const isCreatingGame = ref(false);
  const { problem: sessionProblem, session, rejoinGame } = usePlayerSession({ asHost: true });
  const queryCache = useQueryCache();

  const playlistMode = useFlag('playlist-mode');
  const mode = ref<GameMode>('STANDARD');
  const hostedMode = computed<GameMode>(() => (playlistMode.value ? mode.value : 'STANDARD'));
  const playlist = ref('');
  const choosingPlaylist = computed(() => hostedMode.value === 'PLAYLIST');
  const {
    data: playlists,
    error: playlistsError,
    isLoading: playlistsLoading,
  } = useQuery(() => ({
    ...hostPlaylistsQuery,
    enabled: choosingPlaylist.value,
  }));
  const playlistsProblem = computed(() => {
    const problem = playlistsError.value;
    if (!problem) return '';
    return problem instanceof ApiError && problem.reason ? problem.reason : 'Your playlists could not be read';
  });
  const hostPlaylists = computed(() => playlists.value ?? []);
  watch(hostPlaylists, (listed) => {
    if (playlist.value === '') playlist.value = listed[0]?.id ?? '';
  });

  const spotifySignIn = () => (window.location.href = '/api/spotify/redirect');

  const createGame = async () => {
    if (isCreatingGame.value) return;

    startUserAction(userAction.registrationCreateGame);
    isCreatingGame.value = true;
    try {
      const { error } = await api.POST('/games', {
        body: {
          nickname: nickname.value,
          mode: hostedMode.value,
          playlistId: choosingPlaylist.value ? playlist.value || undefined : undefined,
        },
      });

      if (!error) {
        const hosted = await queryCache.fetch(queryCache.ensure(playerSessionQuery));
        await router.push({ name: route.game, params: { gameCode: hosted.data?.gameCode } });
        return;
      }

      err.value = messageOf('hostGame', error);
    } finally {
      isCreatingGame.value = false;
    }
  };

  watch(nickname, () => {
    err.value = '';
  });
</script>

<template>
  <RegistrationView
    v-model="nickname"
    :error="err || sessionProblem"
    :submitting="isCreatingGame"
    submit-text="Create game"
    submitting-text="Creating game…"
    @submit="createGame"
  >
    <template v-if="playlistMode && !session" #mode>
      <HostGameModeChoice
        v-model="mode"
        v-model:playlist="playlist"
        :error="playlistsProblem"
        :loading="playlistsLoading"
        :playlists="hostPlaylists"
      />
    </template>
    <template v-if="session" #action>
      <HostRejoinAction :session="session" @rejoin="rejoinGame" @spotify-sign-in="spotifySignIn" />
    </template>
  </RegistrationView>
</template>
